import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  InputAdornment,
  Stack,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useSuppliers } from '../../hooks/useSuppliers';
import { Supplier } from '@domain/suppliers/models/Supplier';

interface SupplierSelectBottomSheetProps {
  initialSupplierId?: string;
  excludeSupplierIds?: Set<string>;
  linkedToProductId?: string; // If set, filter out suppliers that already have this product linked
  onSelect: (supplier: Supplier) => void;
  onClose: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

const SupplierSelectBottomSheet: React.FC<SupplierSelectBottomSheetProps> = ({
  initialSupplierId,
  excludeSupplierIds = new Set(),
  linkedToProductId,
  onSelect,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { suppliers, isLoading } = useSuppliers();

  const availableSuppliers = React.useMemo(() => {
    return suppliers.filter(s => {
      if (excludeSupplierIds.has(s.id)) return false;
    
      if (debouncedSearch) {
        const term = debouncedSearch.toLowerCase();
        return s.name.toLowerCase().includes(term) || (s.email?.toLowerCase().includes(term));
      }
      return true;
    });
  }, [suppliers, excludeSupplierIds, linkedToProductId, debouncedSearch]);

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Select supplier</Typography>
          <Box
            component="button"
            onClick={onClose}
            sx={{ display: 'flex', p: 0.5, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            <Close />
          </Box>
        </Stack>
      </DialogHeader>
      <Stack sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }} gap={0}>
        <Stack sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }} spacing={2}>
          <TextField
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : suppliers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              {debouncedSearch ? 'No suppliers found.' : 'No suppliers available.'}
            </Typography>
          ) : (
            <List sx={{ py: 0 }} disablePadding>
              {availableSuppliers.map(supplier => {

                const alreadyLinked = linkedToProductId && supplier.supplierProducts?.some(lp => lp.product?.id === linkedToProductId);
                const alreadyExcluded = excludeSupplierIds.has(supplier.id);
                const isInactive = !supplier.isActive;
                
                
                return (
                  <ListItemButton
                    key={supplier.id}
                    disabled={alreadyExcluded || alreadyLinked || isInactive}
                    divider
                    selected={supplier.id === initialSupplierId}
                    onClick={() => onSelect(supplier)}
                    sx={{
                      py: 2,
                      px: 4,
                    }}

                  >
                    <ListItemText
                      primary={supplier.name}
                      secondary={alreadyExcluded ? 'Already excluded' : alreadyLinked ? 'Already linked to product' : isInactive ? 'Inactive' : supplier.email}
                      sx={{ flex: 1 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default SupplierSelectBottomSheet;
